import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Loader2, CheckCircle, User, Phone, Lock, MapPin, Download } from 'lucide-react';

// Corrected import to use the three exported functions from api2.js
import { requestBrochure, verifyBrochureOTP, completeBrochureAccount } from '../../services/api2.js';

// --- MAIN COMPONENT ---
export const BrochureDownloadModal = ({ isOpen, onClose, collegeId, collegeName, brochureUrl, isLoggedIn, userEmail }) => {
  
  // Determine initial state based on props
  const initialStep = isLoggedIn && userEmail && brochureUrl ? 'download' : 'email';
  
  const [step, setStep] = useState(initialStep); 
  const [email, setEmail] = useState(userEmail || '');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  
  const [accountData, setAccountData] = useState({
    fullName: '',
    mobile: '',
    userType: '',
    password: '',
    city: ''
  });

  // Reset state when modal is closed or opened with different login status
  useEffect(() => {
    if (isOpen) {
        const newInitialStep = isLoggedIn && userEmail && brochureUrl ? 'download' : 'email';
        setStep(newInitialStep);
        setEmail(userEmail || '');
        setError('');
        setOtp('');
        setToken('');
    }
  }, [isOpen, isLoggedIn, userEmail, brochureUrl]);


  // Function to handle direct brochure download/open
  const handleDownload = useCallback(async () => {
    // Only run if a URL is available (either initial or fetched later)
    if (!brochureUrl) {
        setError("Brochure URL is unavailable. Please start the verification process.");
        return;
    }

    setLoading(true);
    setError('');

    try {
      // NOTE: This call is purely for auditing/logging on the backend for a logged-in user. 
      // The backend should respond with the URL or null, allowing us to open the file.
      const response = await requestBrochure({
        email: email, 
        collegeId: collegeId, 
        isLoggedIn: true,
        // The backend should use the existing session token from the interceptor headers
      });
      
      const data = response.data;
      
      // If the backend returns a URL or the initial URL is valid, open it.
      const finalUrl = data.brochureUrl || brochureUrl;

      if (finalUrl) {
        window.open(finalUrl, '_blank');
        // Give time for the browser to open the file before closing the modal
        setTimeout(onClose, 500); 
      } else {
        // Should not happen for logged-in user if brochureUrl is present, but fallback to success
        setStep('success'); 
      }
    } catch (err) {
      console.error("Brochure Download Failed:", err);
      setError(err.response?.data?.message || 'Failed to fetch brochure.');
    } finally {
      setLoading(false);
    }
  }, [collegeId, email, onClose, brochureUrl]);


  // Effect to handle the immediate download step for logged-in users
  useEffect(() => {
    // This effect handles the automatic start of the download process
    if (isOpen && step === 'download' && !loading && isLoggedIn && brochureUrl) {
      handleDownload();
    }
  }, [isOpen, step, loading, isLoggedIn, brochureUrl, handleDownload]);


  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await requestBrochure({
        email,
        collegeId,
        isLoggedIn: false
      });

      const data = response.data;

      if (data.success) {
        // Check if user is fully verified/registered (brochureUrl returned)
        if (data.brochureUrl) {
            window.open(data.brochureUrl, '_blank');
            onClose();
        } else {
            // Requires OTP or account setup
            setStep('otp'); 
        }
      } else {
        setError(data.message || 'Failed to process request');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await verifyBrochureOTP({
        email,
        otp,
        collegeId
      });

      const data = response.data;

      if (data.success) {
        if (data.needsAccount) {
          setToken(data.token);
          setStep('account');
        } else if (data.brochureUrl) {
          window.open(data.brochureUrl, '_blank');
          onClose();
        } else {
          setStep('success');
        }
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountComplete = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await completeBrochureAccount({
        ...accountData,
        email,
        token
      });

      const data = response.data;

      if (data.success) {
        // Handle client-side session update
        if (data.token) {
           // Assuming data.token is an adminToken or similar that needs storage
           // NOTE: The backend should return the standard auth cookie/token on success
           // For simplicity, we just store the returned token if provided
           localStorage.setItem('auth_token', data.token);
        }
        localStorage.setItem('user_email', email);
        
        if (data.brochureUrl) {
          window.open(data.brochureUrl, '_blank');
          onClose();
        } else {
          setStep('success');
        }
      } else {
        setError(data.message || 'Failed to create account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Download Brochure</h3>
              <p className="text-sm text-gray-600 mt-1">{collegeName}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Step 1: Email Input / Auto-Download (for logged-in users) */}
          {(step === 'email' || step === 'download') && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-lg font-bold text-gray-800">
                  {step === 'download' ? 'Initiating Download...' : 'Secure Access'}
                </h4>
                <p className="text-sm text-gray-600">
                  {step === 'download' 
                    ? 'Please wait, checking account and fetching file.' 
                    : 'Enter your email to get the brochure.'}
                </p>
              </div>

              {step === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 sr-only">
                    Enter your email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    We'll send you an OTP to verify your email.
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {step === 'email' && (
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send OTP
                    </>
                  )}
                </button>
              )}
               {step === 'download' && (
                <div className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing Download...
                </div>
              )}
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 'otp' && (
            <form onSubmit={handleOTPVerify} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <p className="text-sm text-gray-600">
                  We've sent a 6-digit OTP to<br />
                  <span className="font-semibold">{email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP
                </label>
                <input
                  type="text"
                  required
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="000000"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  OTP expires in 10 minutes
                </p>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold py-3 rounded-lg hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Verify OTP
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full text-sm text-gray-600 hover:text-gray-800 transition"
              >
                Change email address
              </button>
            </form>
          )}

          {/* Step 3: Account Completion */}
          {step === 'account' && (
            <form onSubmit={handleAccountComplete} className="space-y-4">
              <div className="text-center mb-4">
                <h4 className="text-lg font-bold text-gray-800">Complete Your Account</h4>
                <p className="text-sm text-gray-600">Just a few more details</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={accountData.fullName}
                    onChange={(e) => setAccountData({ ...accountData, fullName: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    required
                    pattern="[0-9]{10}"
                    value={accountData.mobile}
                    onChange={(e) => setAccountData({ ...accountData, mobile: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="10-digit mobile number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  I am a *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAccountData({ ...accountData, userType: 'student' })}
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition ${
                      accountData.userType === 'student'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    🎓 Student
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountData({ ...accountData, userType: 'partner' })}
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition ${
                      accountData.userType === 'partner'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    🤝 Partner
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={accountData.password}
                    onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Minimum 6 characters"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={accountData.city}
                    onChange={(e) => setAccountData({ ...accountData, city: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your city"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !accountData.userType}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-3 rounded-lg hover:shadow-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Complete & Download
                  </>
                )}
              </button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Success!</h3>
              <p className="text-gray-600 mb-6">
                The brochure has been sent to your email address:<br />
                <span className="font-semibold">{email}</span>
              </p>
              <button
                onClick={onClose}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};